/**
 * DXF Actions
 * DXF data management per floor.
 */

import { getCurrentTimestamp } from '../../lib/utils';
import { log, LogCategory } from '../../lib/logger';
import { FloorId } from '../../types/branded';
import type { DXFData, Floor, FloorState } from './types';
import { persistState } from './persistence';

type SetFn = (partial: Partial<FloorState>) => void;
type GetFn = () => FloorState;

export const createDxfActions = (set: SetFn, get: GetFn) => ({
  setDXFData: (floorId: FloorId | string, dxfData: DXFData | null) => {
    const state = get();
    const floor = state.floors.find((f) => f.id === floorId);

    if (!floor) {
      throw new Error(`Floor with id "${floorId}" not found`);
    }

    const updatedFloor: Floor = {
      ...floor,
      mapData: {
        ...floor.mapData,
        dxf: dxfData,
      },
      modified: getCurrentTimestamp(),
    };

    const newFloors = state.floors.map((f) => (f.id === floorId ? updatedFloor : f));

    persistState(newFloors, state.currentFloor);

    set({ floors: newFloors });

    log.info(LogCategory.FLOOR, 'DXF data updated', {
      floorId,
      fileName: dxfData?.fileName,
    });
  },

  updateDXFData: (floorId: FloorId | string, updates: Partial<DXFData>) => {
    const state = get();
    const floor = state.floors.find((f) => f.id === floorId);

    if (!floor || !floor.mapData?.dxf) {
      throw new Error(`Floor with id "${floorId}" not found or has no DXF data`);
    }

    const updatedDXF: DXFData = {
      ...floor.mapData.dxf,
      ...updates,
    };

    const updatedFloor: Floor = {
      ...floor,
      mapData: {
        ...floor.mapData,
        dxf: updatedDXF,
      },
      modified: getCurrentTimestamp(),
    };

    const newFloors = state.floors.map((f) => (f.id === floorId ? updatedFloor : f));

    persistState(newFloors, state.currentFloor);

    set({ floors: newFloors });
  },

  clearDXFData: (floorId: FloorId | string) => {
    get().setDXFData(floorId, null);
    log.info(LogCategory.FLOOR, 'DXF data cleared', { floorId });
  },

  getDXFData: (floorId: FloorId | string) => {
    const floor = get().floors.find((f) => f.id === floorId);
    return floor?.mapData?.dxf ?? null;
  },
});
